import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  TextField,
  Chip,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Paper,
  IconButton,
  LinearProgress,
  FormControlLabel,
  FormControl,
  RadioGroup,
  Radio,
} from '@mui/material';
import {
  Send as SendIcon,
  Search as SearchIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Preview as PreviewIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Lock as LockIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { zohoAuthService } from '@/services/zohoAuth';
import { zohoProjectsApiService } from '@/services/zohoProjectsApi';
import { PlateEditor } from './PlateEditor';

interface Project {
  id: string;
  name: string;
  description?: string;
}

interface MessageFormProps {
  selectedPortal: { id: string; name: string; portal_name: string };
}

export const MessageForm: React.FC<MessageFormProps> = ({ selectedPortal }) => {
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Nouveaux états pour les champs supplémentaires

  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [notifyUsers, setNotifyUsers] = useState(true); // Par défaut, notifier les utilisateurs
  const [postFlag, setPostFlag] = useState<'internal' | 'external'>('internal'); // Par défaut, post interne
  const [projectUsers, setProjectUsers] = useState<{[projectId: string]: Array<{id: string; name: string; email: string; role: string}>}>({});

  const [sending, setSending] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [messageTitle, setMessageTitle] = useState('');
  const [messageContent, setMessageContent] = useState('');

  // Charger les projets du portail
  useEffect(() => {
    const loadProjects = async () => {
      setLoading(true);
      setAllProjects([]);
      setFilteredProjects([]);
      
      try {
        const token = await zohoAuthService.getValidToken();
        if (token) {
          // Utiliser la nouvelle méthode du service zohoProjectsApi qui charge automatiquement tous les projets
          const projectsData = await zohoProjectsApiService.getProjects(token, selectedPortal.id);
          
          // Mise à jour de l'état
          setAllProjects(projectsData);
          setFilteredProjects(projectsData);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des projets:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [selectedPortal.id, selectedPortal.name]);

  // Charger les utilisateurs clients de chaque projet sélectionné
  useEffect(() => {
    const loadProjectClientUsers = async () => {
      if (selectedProjects.length === 0) {
        setProjectUsers({});
        return;
      }

      try {
        const token = await zohoAuthService.getValidToken();
        if (!token) return;

        const usersData: {[projectId: string]: Array<{id: string; name: string; email: string; role: string}>} = {};
        
        // Récupérer les utilisateurs clients de chaque projet individuellement
        for (const projectId of selectedProjects) {
          const projectClientUsers = await zohoProjectsApiService.getProjectUsers(token, selectedPortal.id, projectId);
          
          if (projectClientUsers.length > 0) {
            usersData[projectId] = projectClientUsers;
          } else {
            usersData[projectId] = [];
          }
        }
        
        setProjectUsers(usersData);
        
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs clients:', error);
        setProjectUsers({});
      }
    };

    loadProjectClientUsers();
  }, [selectedProjects, selectedPortal.id]);

  // Calculer le nombre total d'utilisateurs qui seront notifiés
  const totalUsersToNotify = useMemo(() => {
    if (!notifyUsers) return 0;
    
    const uniqueUsers = new Set<string>();
    Object.values(projectUsers).forEach(users => {
      users.forEach(user => uniqueUsers.add(user.id));
    });
    
    return uniqueUsers.size;
  }, [notifyUsers, projectUsers]);

  // Filtrer les projets quand le terme de recherche change
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProjects(allProjects);
    } else {
      const filtered = allProjects.filter(project => 
        project.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProjects(filtered);
    }
  }, [searchTerm, allProjects]);

  // Cette fonction n'est plus nécessaire car on charge tout automatiquement
  // const loadMoreProjects = () => {
  //   if (hasMoreProjects && !loadingMore) {
  //     loadAllProjects(currentPage + 1, true);
  //   }
  // };

  const handleProjectToggle = (projectId: string) => {
    setSelectedProjects(prev => 
      prev.includes(projectId) 
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  const handleSelectAllFiltered = () => {
    const filteredIds = filteredProjects.map(project => project.id);
    setSelectedProjects(prev => {
      const newSelection = [...prev];
      filteredIds.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      return newSelection;
    });
  };

  const handleDeselectAllFiltered = () => {
    const filteredIds = filteredProjects.map(project => project.id);
    setSelectedProjects(prev => prev.filter(id => !filteredIds.includes(id)));
  };

  const getSelectedProjectsInfo = () => {
    return allProjects.filter(project => selectedProjects.includes(project.id));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setAttachments(prev => [...prev, ...files]);
  };

  const handleRemoveFile = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Fonction pour formater le HTML pour la prévisualisation
  const formatHtmlForPreview = (html: string): string => {
    if (!html) return '';
    
    // Créer un élément temporaire pour parser le HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Remplacer les balises <p> vides par des <br> pour préserver les sauts de ligne
    const paragraphs = tempDiv.querySelectorAll('p');
    paragraphs.forEach(p => {
      if (p.innerHTML.trim() === '' || p.innerHTML === '<br>') {
        p.outerHTML = '<br>';
      }
    });
    
    // Remplacer les balises <div> vides par des <br>
    const divs = tempDiv.querySelectorAll('div');
    divs.forEach(div => {
      if (div.innerHTML.trim() === '' || div.innerHTML === '<br>') {
        div.outerHTML = '<br>';
      }
    });
    
    // Nettoyer les balises <br> multiples consécutives
    let cleanedHtml = tempDiv.innerHTML;
    cleanedHtml = cleanedHtml.replace(/<br\s*\/?>\s*<br\s*\/?>/g, '<br><br>');
    cleanedHtml = cleanedHtml.replace(/<br\s*\/?>\s*<br\s*\/?>\s*<br\s*\/?>/g, '<br><br>');
    
    // Supprimer les balises <br> au début et à la fin
    cleanedHtml = cleanedHtml.replace(/^<br\s*\/?>\s*/, '');
    cleanedHtml = cleanedHtml.replace(/\s*<br\s*\/?>$/, '');
    
    return cleanedHtml;
  };

  const handlePreview = () => {
    setPreviewMode(true);
  };

  const handleExitPreview = () => {
    setPreviewMode(false);
  };

  const handleSubmit = async () => {
    if (selectedProjects.length === 0) {
      alert('Veuillez sélectionner au moins un projet');
      return;
    }

    if (!messageTitle.trim()) {
      alert('Veuillez saisir un titre pour le message');
      return;
    }

    if (!messageContent.trim()) {
      alert('Veuillez saisir un contenu pour le message');
      return;
    }

    setSending(true);
    try {
      const result = await zohoProjectsApiService.sendMessageToProjects({
        title: messageTitle,
        content: messageContent,
        tags,
        attachments,
        projectIds: selectedProjects,
        portalId: selectedPortal.id,
        projects: getSelectedProjectsInfo(),
        notifyUsers: notifyUsers,
        projectUsers: projectUsers,
        flag: postFlag,
      });

      if (result.success) {
        const successCount = result.results.filter(r => r.postId).length;
        const errorCount = result.results.filter(r => r.error).length;
        
        if (errorCount === 0) {
          alert(`✅ Message envoyé avec succès à ${successCount} projet(s) !`);
        } else {
          alert(`⚠️ Message envoyé à ${successCount} projet(s), ${errorCount} erreur(s) rencontrée(s).`);
        }
        
        // Afficher les détails des erreurs si il y en a
        if (errorCount > 0) {
          // const errors = result.results.filter(r => r.error);
          // TODO: Afficher les détails des erreurs
        }
        
        // Reset form
        setSelectedProjects([]);
        setTags([]);
        setAttachments([]);
        setMessageTitle('');
        setMessageContent('');
        setPostFlag('internal'); // Reset le flag à la valeur par défaut
      } else {
        alert('❌ Erreur lors de l\'envoi du message');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      alert('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card sx={{ mb: 4, borderRadius: 3 }}>
      <CardHeader
        title={
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
            {previewMode ? 'Prévisualisation du Message' : 'Nouveau Message'}
          </Typography>
        }
        subheader={`Portail actuel: ${selectedPortal.name || selectedPortal.portal_name}`}
        sx={{ pb: 1 }}
        action={
          previewMode && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleExitPreview}
            >
              Retour à l'édition
            </Button>
          )
        }
      />
      
      <CardContent>
        {previewMode ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: 3, backgroundColor: 'background.default' }}>
              <Typography variant="h6" gutterBottom>
                Projets de destination
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {getSelectedProjectsInfo().map((project) => (
                  <Chip key={project.id} label={project.name} color="primary" />
                ))}
              </Box>
              
              {/* Information de notification */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Notifications
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {notifyUsers ? (
                    <>
                      <Chip 
                        label={`${totalUsersToNotify} utilisateur(s) client(s) notifié(s)`} 
                        color="success" 
                        variant="outlined" 
                        size="small" 
                      />
                      <Typography variant="body2" color="text.secondary">
                        Tous les utilisateurs clients des projets sélectionnés recevront une notification
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Chip 
                        label="Aucune notification" 
                        color="default" 
                        variant="outlined" 
                        size="small" 
                      />
                      <Typography variant="body2" color="text.secondary">
                        Aucun utilisateur client ne sera notifié automatiquement
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
              
              <Typography variant="h6" gutterBottom>
                Titre
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                {messageTitle || 'Aucun titre'}
              </Typography>
              
              <Typography variant="h6" gutterBottom>
                Contenu
              </Typography>
              <Box 
                sx={{ 
                  mb: 2, 
                  p: 2, 
                  backgroundColor: 'background.paper', 
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  minHeight: 100,
                  whiteSpace: 'pre-wrap', // Préserver les sauts de ligne
                  wordWrap: 'break-word'
                }}
                dangerouslySetInnerHTML={{ __html: formatHtmlForPreview(messageContent) || 'Aucun contenu' }}
              />
              
              {tags.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Tags ({tags.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {tags.map((tag, index) => (
                      <Chip key={index} label={tag} variant="outlined" size="small" />
                    ))}
                  </Box>
                </Box>
              )}
              
              {/* Affichage du flag de visibilité */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Visibilité
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {postFlag === 'internal' ? (
                    <>
                      <LockIcon fontSize="small" color="primary" />
                      <Chip 
                        label="Interne - Visible uniquement aux utilisateurs du projet" 
                        variant="outlined" 
                        size="small" 
                        color="primary"
                      />
                    </>
                  ) : (
                    <>
                      <PublicIcon fontSize="small" color="primary" />
                      <Chip 
                        label="Externe - Visible aux utilisateurs du projet et aux clients" 
                        variant="outlined" 
                        size="small" 
                        color="primary"
                      />
                    </>
                  )}
                </Box>
              </Box>
              
              {attachments.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Pièces jointes ({attachments.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {attachments.map((file, index) => (
                      <Chip key={index} label={file.name} variant="outlined" size="small" />
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Sélection des projets de destination */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Projets de destination
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sélectionnez les projets auxquels vous voulez envoyer le message
              </Typography>

              {/* Barre de recherche */}
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Rechercher des projets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ mb: 2 }}
              />

              {/* Boutons de sélection globale */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CheckBoxIcon />}
                  onClick={handleSelectAllFiltered}
                  disabled={filteredProjects.length === 0}
                >
                  Tout sélectionner
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CheckBoxOutlineBlankIcon />}
                  onClick={handleDeselectAllFiltered}
                  disabled={filteredProjects.length === 0}
                >
                  Tout désélectionner
                </Button>
              </Box>

              {/* Liste des projets */}
              {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <LinearProgress />
                  <Typography variant="body2" sx={{ textAlign: 'center' }}>
                    Chargement des projets...
                  </Typography>
                </Box>
              ) : (
                <Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <List dense>
                    {filteredProjects.map((project) => (
                      <ListItem key={project.id} disablePadding>
                        <ListItemButton
                          dense
                          onClick={() => handleProjectToggle(project.id)}
                          sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
                        >
                          <Checkbox
                            edge="start"
                            checked={selectedProjects.includes(project.id)}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                          />
                          <ListItemText
                            primary={project.name}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                  
                  {/* Indicateur de chargement automatique */}
                  {loading && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Chargement automatique de tous les projets...
                      </Typography>
                    </Box>
                  )}
                </Paper>
              )}

              {selectedProjects.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Projets sélectionnés ({selectedProjects.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {getSelectedProjectsInfo().map((project) => (
                      <Chip
                        key={project.id}
                        label={project.name}
                        onDelete={() => handleProjectToggle(project.id)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Options de notification */}
            {selectedProjects.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Notifications
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choisissez si vous voulez notifier automatiquement les utilisateurs des projets sélectionnés
                </Typography>
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={notifyUsers}
                      onChange={(e) => setNotifyUsers(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        Notifier automatiquement tous les utilisateurs clients des projets sélectionnés
                      </Typography>
                      {notifyUsers && totalUsersToNotify > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {totalUsersToNotify} utilisateur(s) client(s) seront notifié(s)
                        </Typography>
                      )}
                    </Box>
                  }
                />
                
                {notifyUsers && totalUsersToNotify > 0 && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Utilisateurs clients qui seront notifiés :
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {Object.entries(projectUsers).map(([projectId, users]) => {
                        const project = getSelectedProjectsInfo().find(p => p.id === projectId);
                        return (
                          <Box key={projectId} sx={{ mb: 2 }}>
                            <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                              {project?.name || projectId} ({users.length} utilisateur(s))
                            </Typography>
                            <Box sx={{ ml: 2, mt: 1 }}>
                              {users.map((user) => (
                                <Typography key={user.id} variant="caption" display="block" color="text.secondary">
                                  • {String(user.name || 'Nom inconnu')} ({String(user.email || 'Email inconnu')}) - {String(user.role || 'Rôle inconnu')}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Contenu du message */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Contenu du message
              </Typography>
              
              {/* Titre */}
              <TextField
                fullWidth
                label="Titre du message"
                variant="outlined"
                value={messageTitle}
                onChange={(e) => setMessageTitle(e.target.value)}
                placeholder="Entrez le titre de votre message..."
                sx={{ mb: 2 }}
                required
              />

              {/* Éditeur WYSIWYG */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Contenu du message *
                </Typography>
                <PlateEditor
                  value={messageContent}
                  onChange={setMessageContent}
                  placeholder="Rédigez votre message ici..."
                />
              </Box>



              {/* Tags */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  {tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Ajouter un tag..."
                    size="small"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddTag}
                    disabled={!newTag.trim()}
                    startIcon={<AddIcon />}
                    size="small"
                  >
                    Ajouter
                  </Button>
                </Box>
              </Box>

              {/* Flag de visibilité */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Visibilité du post
                </Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={postFlag}
                    onChange={(e) => setPostFlag(e.target.value as 'internal' | 'external')}
                    row
                  >
                    <FormControlLabel
                      value="internal"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LockIcon fontSize="small" color="primary" />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Interne
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Visible uniquement aux utilisateurs du projet
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="external"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PublicIcon fontSize="small" color="primary" />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Externe
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Visible aux utilisateurs du projet et aux clients
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* Pièces jointes */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Pièces jointes ({attachments.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    id="file-upload"
                  />
                  <label htmlFor="file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<AttachFileIcon />}
                      size="small"
                    >
                      Ajouter des fichiers
                    </Button>
                  </label>
                </Box>
                {attachments.length > 0 && (
                  <Box>
                    {attachments.map((file, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1,
                          mb: 1,
                          backgroundColor: 'background.paper',
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFile(index)}
                          color="error"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Actions */}
            <Box>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={handlePreview}
                  disabled={selectedProjects.length === 0 || !messageTitle || !messageContent}
                >
                  Prévisualiser
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SendIcon />}
                  onClick={handleSubmit}
                  disabled={selectedProjects.length === 0 || !messageTitle || !messageContent || sending}
                >
                  {sending ? 'Envoi en cours...' : 'Envoyer le message'}
                </Button>
              </Box>
              
              {sending && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                    Envoi du message en cours...
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}; 